import React, { useState, useRef, useEffect } from 'react';
import { MoreHorizontal, Trash2, ShieldAlert, CheckCircle, Clock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminRole } from '../../utils/roles';

const CommentCard = ({ comment, onDelete, onReport }) => {
  const { user, isBanned } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);

  const isOwner = user?.id === comment.userId;
  const isAdmin = isAdminRole(user?.role);
  const canDelete = isOwner || isAdmin;
  const isAwaitingReview = comment.status === 'hidden' && comment.hiddenReason === 'PROFANITY';

  // Handle outside click for menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const timeAgo = (dateStr) => {
    const diff = new Date() - new Date(dateStr);
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div className="flex gap-4 p-4 hover:bg-gray-50 rounded-xl transition-colors group relative">
      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-teal-600 text-white font-bold flex items-center justify-center shrink-0 border border-gray-200 text-lg">
        {comment.user?.name ? comment.user.name.charAt(0).toUpperCase() : '?'}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-bold text-sm text-gray-900">{comment.user?.name}</span>
            {comment.user?.isVerified && <CheckCircle size={14} className="text-blue-500" fill="currentColor" />}
            <span className="text-sm text-gray-500">@{comment.user?.handle}</span>
            <span className="text-gray-300 mx-1">•</span>
            <span className="text-xs text-gray-500">{timeAgo(comment.createdAt)}</span>
          </div>

          <div className="relative" ref={menuRef}>
            <button 
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors opacity-0 group-hover:opacity-100 sm:opacity-100"
            >
              <MoreHorizontal size={18} />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-100 shadow-lg rounded-xl overflow-hidden z-10 animate-fade-in">
                {canDelete && (
                  <button 
                    onClick={() => { setShowMenu(false); onDelete(comment.id); }}
                    className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                )}
                <button 
                  onClick={() => { setShowMenu(false); if (!isBanned) onReport(comment.id); }}
                  disabled={isBanned}
                  className={`w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 ${isBanned ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <ShieldAlert size={16} />
                  Report
                </button>
              </div>
            )}
          </div>
        </div>

        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap break-words">
          {comment.body}
        </p>

        {isAwaitingReview && (
          <div className="mt-2 inline-flex items-center gap-1.5 bg-yellow-50 text-yellow-800 text-xs font-medium px-2.5 py-1 rounded-md border border-yellow-200">
            <Clock size={12} />
            Awaiting Review
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentCard;
